import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send } from "lucide-react";
import { api, formatApiError, lastSeenInfo } from "@/lib/api";
import { Avatar } from "@/components/chat/ConversationList";

const time = (iso) => new Date(iso).toLocaleString("it-IT", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

export const ChatThread = ({ convId, meId, onActivity }) => {
  const [conv, setConv] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const count = useRef(0);

  const load = useCallback(() => api.get(`/conversations/${convId}`).then((r) => {
    setConv(r.data);
    if (r.data.messages.length !== count.current) {
      count.current = r.data.messages.length;
      setTimeout(() => endRef.current?.scrollIntoView({ block: "end" }), 50);
      onActivity();
      window.dispatchEvent(new Event("messages:read"));
    }
  }).catch(() => setConv(false)), [convId, onActivity]);

  useEffect(() => {
    count.current = 0;
    setConv(null);
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);

  const send = async (e) => {
    e.preventDefault();
    if (sending || !text.trim()) return;
    setSending(true);
    try {
      await api.post(`/conversations/${convId}/messages`, { text });
      setText("");
      await load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSending(false); }
  };

  if (conv === null) return <p className="eyebrow p-10">Caricamento…</p>;
  if (!conv) return <p className="font-serif text-2xl p-10 text-neutral-500" data-testid="chat-not-found">Conversazione non trovata</p>;
  const seen = lastSeenInfo(conv.other.last_seen);

  return (
    <div className="flex flex-col h-full" data-testid="chat-thread">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-white/5">
        <Link to="/messaggi" className="md:hidden text-neutral-400 hover:text-white" data-testid="chat-back-link" aria-label="Indietro"><ArrowLeft className="w-4 h-4" /></Link>
        <Avatar person={conv.other} size="w-10 h-10" />
        <div className="min-w-0">
          {conv.other.role === "hostess"
            ? <Link to={`/hostess/${conv.other.id}`} className="font-serif text-xl hover:text-[#D4AF37] truncate block" data-testid="chat-other-name">{conv.other.name}</Link>
            : <p className="font-serif text-xl truncate" data-testid="chat-other-name">{conv.other.name}</p>}
          {seen.label && <p className="text-[11px] text-neutral-500 flex items-center gap-1.5"><span className={`w-1.5 h-1.5 rounded-full ${seen.online ? "bg-emerald-400" : "bg-neutral-600"}`} />{seen.label}</p>}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-6 space-y-3" data-testid="chat-messages">
        {conv.messages.length === 0 && <p className="text-sm text-neutral-500 text-center py-10" data-testid="chat-empty">Scrivi il primo messaggio per organizzare l'evento.</p>}
        {conv.messages.map((m) => {
          const mine = m.sender_id === meId;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${m.id}`}>
              <div className={`max-w-[78%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words ${mine ? "bg-[#D4AF37] text-[#0B0C0E]" : "bg-[#181B20] text-neutral-100 border border-white/5"}`}>
                {m.text}
                <span className={`block text-[10px] mt-1 font-mono ${mine ? "text-[#0B0C0E]/60" : "text-neutral-500"}`}>{time(m.created_at)}</span>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      {conv.other.unavailable ? (
        <p className="px-5 py-4 border-t border-white/5 text-sm text-neutral-500" data-testid="chat-unavailable">Questo utente non è più disponibile.</p>
      ) : (
        <form onSubmit={send} className="flex items-end gap-3 px-5 py-4 border-t border-white/5" data-testid="chat-form">
          <textarea rows={1} value={text} maxLength={2000} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) send(e); }}
            placeholder="Scrivi un messaggio…" className="lux-input resize-none max-h-32" data-testid="chat-input" />
          <button disabled={sending || !text.trim()} className="btn-gold !px-4 !py-3" data-testid="chat-send-button" aria-label="Invia"><Send className="w-4 h-4" /></button>
        </form>
      )}
    </div>
  );
};
